// English (default). Interface text for the translated pages.
// Content lists (tours, FAQs, programmes...) stay in src/data/*.js in English;
// other language files translate them under `home.data` and `programmes`.
export default {
  // Display names for cities and languages in guide data. English keeps
  // the data as-is, except "Deutsch", which is written in German there.
  names: {
    places: {},
    languages: { Deutsch: "German" },
  },

  header: {
    openMenu: "Open menu",
    closeMenu: "Close menu",
    getThePass: "Get the pass",
    language: "Language",
    changeLanguage: "Change language",
    myDashboard: "My dashboard",
    myProfile: "My profile",
    signOut: "Sign out",
  },

  nav: {
    visitorPass: "Lisbon Visitor Pass",
    tukTuk: "Tuk Tuk Tours",
    guides: "Private Guides",
    portugalTours: "Portugal Tours",
    agencies: "Agencies & Groups",
  },

  footer: {
    description: "Discover Portugal through authentic tours and local experiences.",
    company: "Company",
    aboutUs: "About us",
    dmcServices: "DMC Services",
    supplierLogin: "Supplier login",
    guidePortal: "Guide portal",
    becomeAffiliate: "Become an affiliate",
    tours: "Tours",
    cityTours: "City tours",
    tukTukTours: "Tuk Tuk Tours",
    customizeTrip: "Customize your trip",
    contact: "Contact",
    paymentMethods: "Payment methods",
    contracts: "Contracts",
    privacyPolicy: "Privacy Policy",
  },

  // ---------------------------------------------------------------- Home
  home: {
    meta: {
      title: "Lisbon Tuk Tuk Tours & Private Guides | Guiders.pt",
      description:
        "Private tuk tuk tours, the hop-on hop-off Lisbon Visitor Pass from €29, licensed private guides and day trips across Portugal. Book with local experts.",
    },
    hero: {
      eyebrow: "Lisbon Visitor Pass · 4 hours or full day",
      title: "Hop on, hop off. By tuk tuk.",
      lead: "One pass for electric tuk tuks with local guides, up Lisbon's hills to the viewpoints. Ride, explore, hop back on, as often as you like.",
      getPass: "Get the pass · from {price}",
      learnMore: "Learn more →",
      rating: "★ {rating} rating",
      validity: "Valid from your first ride",
      electric: "100% electric",
      videoAlt: "Guests on a Guiders tuk tuk at a Lisbon viewpoint",
    },
    b2b: {
      text: "Travel agency, tour operator or group?",
      link: "Get net rates & our DMC programme →",
    },
    tuktuk: {
      title: "Private tuk tuk tours",
      fromPerTukTuk: "from, per tuk tuk",
      book: "Book",
    },
    trips: {
      eyebrow: "Beyond Lisbon · All of Portugal",
      title: "City tours & day trips",
    },
    dmc: {
      eyebrow: "For travel agencies, tour operators & companies",
      title: "Your Destination Management Company in Portugal",
      lead: "Groups, FIT and incentives, designed and run end-to-end by a local team, across the whole country. Net rates, one contact, 24/7 on the ground.",
      requestProposal: "Request a proposal",
      replyWithin: "Reply within {time}",
    },
    faq: {
      title: "Good to know",
      categories: "FAQ categories",
    },
    // Translations of src/data/homepage.js lists go here in other languages
    data: {},
  },

  // ------------------------------------------------- Meet your guide section
  guides: {
    eyebrow: "Licensed private guides · All of Portugal",
    title: "Meet your guide",
    lead: "Book a certified local guide for a walking tour, a day trip or your whole stay. Choose by city and by the language you speak.",
    city: "City",
    allCities: "All cities",
    language: "Language",
    allLanguages: "All languages",
    ctaTitle: "Need a private guide?",
    ctaText:
      "Half day, full day or a whole trip, in any city. Tell us your plans and we'll match you with a licensed guide and send you a quote.",
    ctaButton: "Request a private guide",
    licensed: "Licensed guide",
    toursIn: "Tours in",
    speaks: "Speaks",
    more: "+{count} more",
    noMatch: "No guide matches that city and language yet.",
    showAll: "Show all guides",

    form: {
      title: "Request a private guide",
      intro:
        "Tell us what you have in mind. We'll match you with a licensed guide and send you a quote. No payment needed, and nothing is booked until you confirm.",
      stepTrip: "Your trip",
      stepGroup: "Your group",
      stepDetails: "Your details",
      howLong: "How long?",
      durations: [
        { label: "Half day", hint: "Up to 4 hours" },
        { label: "Full day", hint: "Up to 8 hours" },
        { label: "Several days", hint: "Multi-day trip" },
      ],
      date: "Date",
      dates: "Dates",
      chooseDate: "Choose a date",
      firstAndLastDay: "First and last day",
      startTime: "Start time",
      startTimes: ["Morning", "Afternoon", "Evening", "Flexible"],
      guideNeededOn: "Guide needed on",
      multiDayCoverages: ["Every day", "Some of the days", "Not sure yet"],
      flexibleDates: "My dates are flexible",
      where: "Where?",
      whereHintSingle: "Pick one or more",
      whereHintMulti: "Pick every place you'd like to visit",
      somewhereElse: "Somewhere else? Tell us where (e.g. Madeira, Douro Valley)",
      people: "Number of people",
      whoTravelling: "Who's travelling?",
      groupTypes: [
        "Couple or friends",
        "Family with children",
        "School or university group",
        "Company or incentive group",
        "Clients of my travel agency",
        "Other",
      ],
      language: "Language",
      chooseOne: "Choose one",
      anotherLanguage: "Another language",
      whichLanguage: "Which language?",
      whichLanguagePlaceholder: "e.g. Italian, Japanese",
      extras: "Extras",
      extrasHint: "Optional, pick any",
      extrasOptions: [
        "Transport with driver",
        "Hotel pick-up",
        "Entrance tickets",
        "Lunch or restaurant booking",
        "Wine or food tasting",
        "Wheelchair accessible",
      ],
      anythingElse: "Anything else? (optional)",
      anythingElsePlaceholder:
        "What you'd like to see, interests, pick-up point, accessibility needs, children's ages...",
      name: "Name",
      email: "Email",
      phone: "Phone (optional)",
      phonePlaceholder: "Phone number",
      company: "Company (optional)",
      companyPlaceholder: "If booking for clients",
      stillNeeded: "Still needed: {fields}",
      missing: {
        date: "date",
        where: "where",
        language: "language",
        name: "name",
        email: "email",
        phone: "a valid phone number (or leave it empty)",
      },
      submit: "Request a quote",
      preferChat: "Prefer to chat?",
      whatsappUs: "WhatsApp us",
      orEmail: "or email",
      sentTitle: "Request sent",
      sentText:
        "Thanks, {name}. We'll check guide availability for {dates} and email you a quote at {email}.",
      done: "Done",
      daysCount: "{count} days",
      dateRangeJoin: "{start} to {end}",
      error: "We couldn't send your request. Please try again, or email support@guiders.pt.",
    },
  },

  // ------------------------------------------- Quote form (tours, programmes)
  quote: {
    title: "Request a quote",
    intro:
      "This tour is available upon request. Tell us your preferred date and group size and we'll send you a personalised price quote.",
    name: "Name",
    email: "Email",
    phone: "Phone number",
    phonePlaceholder: "Enter your phone number",
    preferredDate: "Preferred date",
    participants: "Participants",
    notes: "Anything else we should know? (optional)",
    notesPlaceholder: "Pick-up location, preferred start time, special requests...",
    submit: "Request a quote",
    noPayment: "No payment required. You can also {email} or {whatsapp}.",
    emailUs: "email us",
    whatsapp: "send a WhatsApp message",
    error:
      "There has been an error sending your quote request. If this error persists please contact support.",
    sentTitle: "Request sent",
    sentText: "Thanks! We'll get back to you at {email} with a quote soon.",
  },

  // ---------------------------------------------------------- Portugal DMC
  dmc: {
    meta: {
      title: "Portugal DMC | Guiders.pt",
      description:
        "Guiders.pt is a B2B Destination Management Company (DMC) and receptive tour operator in Portugal. We supply licensed guides, coach hire, transfers, and ground operations for tour operators, MICE planners, and travel agencies.",
    },
    nav: {
      whoWeHelp: "Who we help",
      services: "Our services",
      guides: "Our guides",
      whyUs: "Why Guiders",
      programmes: "Sample programmes",
      requestProposal: "Request a proposal",
    },
    hero: {
      eyebrow: "Portugal DMC · Destination management & ground services",
      title: "Your Portugal DMC",
      titleAccent: "for guides, transport & ground services.",
      lead: "The local receptive operator behind successful Portugal programmes for tour operators, inbound DMCs, MICE/incentive planners and travel agencies. We manage guides, transport, itineraries and on-the-ground logistics, so your team doesn't have to.",
      requestProposal: "Request a proposal",
      seeServices: "See what we cover",
      trustLicensed: "Licensed & insured",
      trustSupport: "24/7 ground support",
      trustFast: "Fast proposal turnaround",
      imageAlt: "Portugal coastal landscape",
      cardTitle: "One local team",
      cardText: "From quote to departure",
    },
    who: {
      eyebrow: "Who we work with",
      title: "Built for the travel trade: DMCs, tour operators & MICE planners.",
      lead: "We work behind the scenes so your clients experience a seamless programme.",
      items: [
        { title: "Inbound DMCs", text: "Extend your destination coverage with a reliable Portugal operations partner on the ground." },
        { title: "Tour Operators", text: "Ground services, guides and transport for FIT, group and multi-day programmes." },
        { title: "MICE & Incentive Planners", text: "Transport, logistics and local coordination for incentives, events and executive groups." },
        { title: "Travel Designers & Agencies", text: "A trusted local team for bespoke Portugal experiences and high-value clients." },
      ],
    },
    services: {
      eyebrow: "What we handle",
      title: "Everything your Portugal programme needs on the ground.",
      lead: "From a single airport transfer to a multi-day incentive, we build the ground operation around your client, itinerary and requirements.",
      items: [
        {
          label: "Guides",
          title: "Private local guides",
          text: "Licensed guides for half-day, full-day and multi-day programmes, with English, French, Spanish and Portuguese language capability.",
          items: ["Private tours", "Group programmes", "Multi-day itineraries", "Local expertise"],
          alt: "Portugal private tour guide",
        },
        {
          label: "Transport",
          title: "Coaches, drivers & transfers",
          text: "Licensed transport from 16-seat minicoaches to 55-seaters, with professional drivers experienced in group logistics and airport movements.",
          items: ["Airport transfers", "16–55 seat coaches", "Executive transport", "Group logistics"],
          alt: "Executive coach for Portugal group travel",
        },
        {
          label: "Programmes",
          title: "Tailor-made itineraries",
          text: "Bespoke routing built around your client profile, budget, interests and group size, from wine programmes to corporate incentives.",
          items: ["Custom routing", "Wine & gastronomy", "Corporate incentives", "Multi-region programmes"],
          alt: "Tailor-made Portugal itinerary",
        },
      ],
      ctaTitle: "Not sure what you need?",
      ctaText: "Send us your brief and we'll help build the ground plan.",
      ctaButton: "Send us your brief",
    },
    guides: {
      eyebrow: "Private guide services",
      title: "Meet the guides who lead your groups",
      lead: "Every guide we place with your clients is a licensed professional, not a freelancer we found last week. Filter by city and language to see who can lead your next group.",
      licenceLabel: "What a licence means",
      licenceTitle: "Guiding in Portugal is a regulated profession.",
      licenceText:
        "To guide groups inside monuments, museums and heritage sites, a guide needs a national professional licence. They only get it after passing a full set of exams, so your clients get facts, not guesswork.",
      exams: [
        { title: "History, art & heritage", text: "Written exams on Portuguese history, art history, architecture and the country's monuments and museums." },
        { title: "Geography & culture", text: "Portugal's regions, landscapes, traditions, gastronomy and wine, so every stop on the route has context." },
        { title: "Every language they guide in", text: "Written and oral language exams for each language on their licence. If a guide offers French, they've been tested in French." },
        { title: "Tourism law & practical guiding", text: "Professional rules and ethics, plus a practical assessment guiding a group on site." },
      ],
    },
    problem: {
      eyebrow: "Your Portugal operation, covered",
      title: "Your client sees the experience.",
      titleAccent: "You need everything behind it to work.",
      p1: "A late coach, missing guide or last-minute change doesn't just create an operational problem. It reflects on your brand.",
      p2: "That's why we work as an extension of your team, not simply another supplier. One local coordinator, one point of contact and a team on the ground when plans change.",
      points: ["Reliable execution", "Clear communication", "Fast problem solving"],
    },
    why: {
      eyebrow: "Why operators trust us",
      title: "We operate like",
      titleAccent: "part of your team.",
      p1: "You shouldn't need to chase three suppliers to find out whether a coach arrived or a guide has been confirmed.",
      p2: "One coordinator manages the details from quote through departure, with local support when something changes.",
      link: "Talk to our team →",
      items: [
        { title: "One coordinator", text: "A single DMC contact from quote to departure. No unnecessary hand-offs." },
        { title: "Licensed & insured", text: "Licensed guides and passenger-insured vehicles for compliant programme delivery." },
        { title: "Transparent pricing", text: "Clear quotes with detailed service breakdowns and trade rates for operators." },
        { title: "24/7 ground support", text: "Local support for delays, weather changes and last-minute itinerary adjustments." },
      ],
    },
    coverage: {
      eyebrow: "Our coverage",
      title: "From Lisbon to Porto and everywhere in between.",
      lead: "As your inbound tour operator and receptive DMC, we run programmes across Portugal's key cities, wine regions and coastal destinations.",
      previous: "Previous regions",
      next: "Next regions",
      regions: ["Lisbon", "Sintra & Cascais", "Porto", "Douro Valley", "Algarve", "Aveiro"],
    },
    programmes: {
      eyebrow: "Programmes we can build",
      title: "Your next Portugal programme could look like this.",
      cardLabel: "Sample programme · {duration}",
      route: "Route: {route}",
      viewFull: "View the full programme →",
      cases: [
        { label: "Group programme", title: "Lisbon · Sintra · Cascais", text: "Guides, private coach transport, airport movements and bespoke sightseeing across a multi-day programme.", alt: "Lisbon and Sintra programme" },
        { label: "Incentive", title: "Porto · Douro Valley", text: "Premium transport, private guiding and wine-focused experiences designed around an international group.", alt: "Douro Valley wine programme" },
        { label: "Tour operator", title: "Lisbon · Alentejo · Algarve", text: "Multi-region routing combining transport, local guides and coordinated ground services.", alt: "Algarve Portugal programme" },
      ],
    },
    steps: {
      eyebrow: "How it works",
      title: "From brief to programme, without the back-and-forth.",
      items: [
        { title: "Send your brief", text: "Dates, group size, destinations and anything your client needs." },
        { title: "We build the ground plan", text: "We review the requirements and work through the logistics with our local team." },
        { title: "Receive your proposal", text: "Clear services and pricing you can review and take back to your client." },
        { title: "We deliver on the ground", text: "Your dedicated contact coordinates the programme through to departure." },
      ],
    },
    contact: {
      memberOf: "Member of",
      eyebrow: "Plan your next programme",
      title: "Tell us what you're planning.",
      titleAccent: "We'll handle the ground.",
      lead: "Send us the basics and our team will come back with availability, recommendations and a proposal.",
    },
    form: {
      thanks: "Thanks, {name}.",
      thanksText: "We've received your Portugal programme enquiry. Our team will be in touch shortly.",
      sendAnother: "Send another enquiry",
      title: "Request a proposal",
      intro: "Give us the essentials. We'll take it from there.",
      name: "Name / company *",
      namePlaceholder: "Your name or company",
      email: "Work email *",
      emailPlaceholder: "you@company.com",
      programmeType: "Programme type *",
      selectOne: "Select one",
      programmeTypes: [
        "Group tour",
        "Incentive",
        "Corporate event",
        "FIT / private travel",
        "Tour operator programme",
        "Other",
      ],
      groupSize: "Group size *",
      details: "Tell us about the programme *",
      detailsPlaceholder:
        "Dates, destinations, group size, services required, budget or anything else we should know...",
      submit: "Request my proposal",
      note: "Your information is used only to respond to your enquiry.",
      errors: {
        name: "Please enter your name or company.",
        email: "Please enter your work email.",
        emailInvalid: "Please enter a valid email address.",
        programme: "Please select a programme type.",
        groupSize: "Please select your group size.",
        details: "Please tell us about your programme.",
      },
      sendError: "Something went wrong sending your enquiry. Please email us directly at support@guiders.pt.",
    },
    faq: [
      {
        q: "What services does Guiders.pt provide as a Portugal DMC?",
        a: "Guiders.pt provides licensed tour guides, executive coach and minicoach transport (16 to 55 seats), airport transfers, custom B2B itinerary planning, and 24/7 on-the-ground support across Lisbon, Porto, Douro Valley, Algarve, and Alentejo.",
      },
      {
        q: "Who does Guiders.pt partner with in the travel industry?",
        a: "We work exclusively with travel trade partners including tour operators, international DMCs, MICE and corporate incentive planners, and travel agencies requiring receptive ground operations in Portugal.",
      },
    ],
  },

  // -------------------------------------------- Tour pages & booking form
  // FAQ answers are HTML (links)
  tour: {
    "errorTitle": "Unable to load activity",
    "tryAgain": "Try Again",
    "useDemo": "Use Demo Data",
    "loading": "Loading tour details...",
    "notFound": "Tour not found",
    "description": "Description",
    "showMore": "Show more",
    "showLess": "Show less",
    "route": "Route",
    "itinerary": "Itinerary",
    "firstDeparture": "First departure",
    "lastDeparture": "Last departure",
    "frequency": "Frequency",
    "listView": "List view",
    "mapView": "Map view",
    "pickupHere": "You can request pickup here.",
    "directions": "Directions",
    "pass": {
      "ownPaceTitle": "Explore at your own pace",
      "ownPaceText": "Hop off at any stop, wander around, and jump back in when you're done. You can also request pickup from any of our pickup stops.",
      "validityTitle": "Extended validity",
      "validityText": "Use your ticket until 12 months after your purchase",
      "guideTitle": "Local guide",
      "guideText": "Our friendly local guides will teach you everything you have to know about the city"
    },
    "availableLanguages": "Available Languages",
    "languagesList": "English, Portuguese, Spanish, French",
    "digitalTicket": "Digital Ticket",
    "digitalTicketText": "Print your ticket or show it to your guide",
    "highlights": "Tour Highlights",
    "included": "What's included",
    "whatToExpect": "What to expect",
    "additionalInfo": "Additional information",
    "notSuitableFor": "Not suitable for",
    "accessibility": "Accessibility",
    "meetingPoint": "Meeting point",
    "boardAnyStop": "You can board at any of the route's stops. The favored stop is",
    "youMightLike": "You might also like",
    "cantFind": "Can't find the tour you want? {email} or {whatsapp} telling us where you'd like to visit in Portugal and your group size. We'll plan the tour and send you a price quote!",
    "emailUs": "Email us",
    "whatsapp": "send a WhatsApp message",
    "cancelPolicy": "Cancel up to 48 hours prior to the start of your experience for a full refund",
    "from": "from",
    "priceUponRequest": "Price upon request. No payment required.",
    "smallDeposit": "Reserve your tour with a small deposit.",
    "requestQuote": "Request a quote",
    "checkAvailability": "Check availability",
    "faq": [
      {
        "q": "Booking confirmation",
        "a": "After paying for your booking, you'll receive an email, to the email you provided, with your booking details. If you didn't receive an email please contact us at <a class='link' href='mailto:support@guiders.pt'>support@guiders.pt</a> or message us at <a class='link' href='https://wa.me/351920043375'>+351 920 043 375</a>"
      },
      {
        "q": "I'm running late to my activity. What do I do?",
        "a": "If you are running late to your activity please let us know by emailing us at <a class='link' href='mailto:support@guiders.pt'>support@guiders.pt</a> or message us at <a class='link' href='https://wa.me/351920043375'>+351 920 043 375</a>."
      },
      {
        "q": "I want to cancel my activity",
        "a": "If you want to cancel your activity email us at <a class='link' href='mailto:support@guiders.pt'>support@guiders.pt</a> or message us at <a class='link' href='https://wa.me/351920043375'>+351 920 043 375</a>."
      },
      {
        "q": "Cancellations and refunds",
        "a": "If you cancel your booking within the cancellation policy, your refund will be processed via the same payment method you used to book the activity.\n\nIf your booking doesn't go as planned or you are unhappy with your experience and would like to request a refund, please send us an email to <a class='link' href='mailto:support@guiders.pt'>support@guiders.pt</a> or message us at <a class='link' href='https://wa.me/351920043375'>+351 920 043 375</a> telling us the reason(s) why you'd like a refund. Refund requests are evaluated on a case-by-case basis.\n\nIn the unlikely event that we need to cancel your booking, you are always entitled to a full refund. If you've received a cancellation confirmation email from us, your refund will be processed via the same payment method you used to book the activity."
      }
    ]
  },

  booking: {
    "bookNow": "Book now",
    "reserveSpot": "Reserve your spot",
    "name": "Name",
    "phone": "Phone number",
    "phonePlaceholder": "Enter your phone number",
    "email": "Email",
    "experienceType": "What type of experience do you want?",
    "private": "Private",
    "shared": "Shared",
    "sharedTooltip": "Split the cost with other participants and meet people",
    "passValidity": "Your ticket is valid for up to 12 months from the purchase date. We only ask for the date for logistical purposes.",
    "date": "Date",
    "soldOut": "Sold out",
    "availableTickets": "Available tickets: {count}",
    "time": "Time",
    "chooseDateFirst": "Choose a date to see the available times",
    "otherTime": "{email} or {whatsapp} if you would like to schedule the tour at a different time than those available or if your group is larger than the accepted limit.",
    "emailUs": "Email us",
    "whatsapp": "send a WhatsApp message",
    "participants": "Participants",
    "upgradeTitle": "Upgrade your ticket",
    "upgradeText": "Get free access to the Santa Justa Lift, Funiculars and Trams for 24 hours",
    "orderDetails": "Order details:",
    "payGuide": "Price to pay to the guide:",
    "payNow": "Price to pay now:",
    "total": "Total:",
    "leadPassenger": "Lead passenger",
    "ticket": "Ticket",
    "tickets": "Tickets",
    "previous": "Previous",
    "next": "Next",
    "reserveFor": "Reserve for {price}",
    "confirmPay": "Confirm and pay",
    "error": "There has been an error booking your tour. If this error persists please contact support."
  },

  // -------------------------------------------------------- Programme page
  programmePage: {
    back: "← Portugal DMC",
    eyebrow: "Sample programme",
    requestQuote: "Request a quote",
    seeItinerary: "See the itinerary",
    included: "What’s included",
    itineraryEyebrow: "Itinerary",
    dayByDay: "Day by day",
    day: "Day {n}",
    moreEyebrow: "Sample programmes",
    moreTitle: "More ways to see Portugal",
    allProgrammes: "All programmes →",
    ctaTitle: "Want this programme for your group?",
    ctaText:
      "Every sample programme is a starting point. Tell us your dates and group size and we’ll adapt hotels, activities and pacing to your clients, then send you a quote.",
    orEmail: "or email",
    priceOnRequest: "Price on request",
    quoteIntro:
      "Tell us your preferred dates and group size. We'll adapt this programme to your clients and send you a quote.",
    nav: {
      included: "What's included",
      itinerary: "Itinerary",
      dmc: "Portugal DMC",
    },
    metaFallback: "{title}: {duration} Portugal itinerary | Guiders",
    breadcrumbHome: "Home",
  },

  // Translations of src/data/programmes.js go here in other languages,
  // keyed by slug
  programmes: {},

  // Lisbon Visitor Pass page (/lisbon-visitor-pass). The facts in
  // src/data/visitorPass.js fill the {placeholders}: {stops}, {pickups},
  // {wait} (minutes), {hours}, {max} (people per tuk tuk), {age}, {months}.
  // Lists line up with that file item by item (same order).
  visitorPass: {
    meta: {
      title: "Lisbon Visitor Pass | Hop-on Hop-off Tuk Tuk Pass from €{price}",
      description:
        "Ride electric tuk tuks between {stops} stops in Lisbon's old town for 4 hours or a full day. Request a pickup at {pickups} stops, average wait under {wait} minutes. From €{price}.",
      name: "Lisbon Visitor Pass: hop-on hop-off tuk tuk pass",
      offer: "{label} pass",
      touristTypes: ["First-time visitors", "Cruise passengers", "Families", "Budget travellers"],
    },
    nav: {
      how: "How it works",
      forYou: "Is it for you?",
      route: "The route",
      prices: "Prices",
      faq: "FAQ",
    },
    choose: "Choose your pass",
    chooseFrom: "Choose your pass · from €{price}",
    perPerson: "/ person",
    getPlan: "Get the {label} pass",
    hero: {
      eyebrow: "Lisbon Visitor Pass · Hop-on hop-off by tuk tuk",
      title: "Lisbon's hills, without the climb.",
      lead:
        "Ride electric tuk tuks between {stops} stops in the old town. Hop off to explore, then request your next ride from your phone. A local driver-guide picks you up in minutes.",
      how: "How it works →",
      facts: [
        "<b>4 hours</b> or a <b>full day</b>",
        "<b>{pickups}</b> pickup stops",
        "Average wait <b>under {wait} min</b>",
        "<b>100%</b> electric",
      ],
      videoLabel: "Guests riding a Guiders tuk tuk through Lisbon",
    },
    facts: {
      validity: "Unused passes are valid for {months} months",
    },
    problems: {
      eyebrow: "Why a tuk tuk pass",
      title: "Lisbon is beautiful. Getting around it is hard work.",
      lead:
        "The old town's best views sit at the top of steep, cobbled hills. The pass takes the effort out of it, so your energy goes on the city, not the climb.",
      items: [
        {
          problem: "Lisbon is built on seven steep hills",
          answer: "Ride up to the viewpoints, walk the easy bits. Cobbles and stairs are the tuk tuk's job.",
        },
        {
          problem: "Tram 28 queues can take 30–45 minutes in summer",
          answer: "Request a tuk tuk from your phone. The average wait is under {wait} minutes.",
        },
        {
          problem: "Big sightseeing buses can't get into Alfama",
          answer: "Tuk tuks fit the narrow lanes of the old town, where the best views are.",
        },
      ],
    },
    steps: {
      eyebrow: "How it works",
      title: "Like having your own tuk tuk on call",
      lead: "Four steps, and no app to download.",
      items: [
        {
          title: "Buy your pass online",
          text: "Choose 4 hours or a full day. Your e-ticket arrives by email straight away, so you can buy it on the day.",
        },
        {
          title: "Go to any pickup stop",
          text: "There are {pickups} pickup stops on the route, from Praça da Figueira to the Graça viewpoints.",
        },
        {
          title: "Request your tuk tuk",
          text: 'Tap "request a driver" on your e-ticket page (or message the number on it). The average wait is under {wait} minutes.',
        },
        {
          title: "Ride, hop off, repeat",
          text: "Your local driver-guide takes you to the next stop. Explore, have lunch, then request the next ride when you're ready.",
        },
      ],
      goodToKnow: "Good to know",
      reassurance: [
        "Tuk tuks run {hours}",
        "Shared rides, up to {max} people per tuk tuk",
        "Your hours start at your first ride, not when you buy",
        "Unused passes are valid for {months} months",
        "100% electric tuk tuks",
      ],
    },
    travellers: {
      eyebrow: "Is it for you?",
      title: "Which traveller are you?",
      lead: "Pick the one that sounds like you for tips and the pass we'd recommend.",
      tabsLabel: "Traveller type",
      recommended: "Recommended: {label} pass",
      weRecommend: "We'd recommend",
      planName: "{label} pass",
      compare: "Compare all passes",
      types: [
        {
          label: "First time in Lisbon",
          title: "See the old town's highlights without planning a thing",
          text: "The route links Lisbon's classic sights: the cathedral, the castle hill, Alfama and the best viewpoints. Your driver-guide tells you what you're looking at and where locals eat, so you get your bearings on day one.",
          tips: [
            "Start at Praça da Figueira and ride up to the Sé (cathedral)",
            "Walk Alfama's lanes down to the Portas do Sol viewpoint",
            "Finish at Senhora do Monte, the highest viewpoint, for the big view",
          ],
        },
        {
          label: "On a cruise",
          title: "Make the most of a port day, on your own schedule",
          text: "Lisbon's cruise terminal sits at the foot of Alfama, a short walk from the Panteão Nacional pickup stop. No group to wait for and no fixed timetable: you decide when to head back.",
          tips: [
            "Buy before you dock: the e-ticket arrives by email instantly",
            "Tuk tuks run {hours}; keep an eye on your all-aboard time",
            "4 hours covers the main viewpoints with time to spare",
          ],
        },
        {
          label: "With kids",
          title: "No pushing a stroller up Lisbon's hills",
          text: "Little legs get a rest between every stop, and the tuk tuk ride itself is half the fun. Stop for ice cream, a playground or a nap, then request the next ride when everyone's ready.",
          tips: [
            "Children from {age} years can ride; infants under {age} can't, for safety",
            "Alfama's lanes have stairs: a baby carrier is easier than a stroller",
            "The full-day pass lets you plan the day around naps and meals",
          ],
        },
        {
          label: "Taking it easy",
          title: "All the views, without the climbs",
          text: "Lisbon's steep, cobbled streets are tiring. Let the tuk tuk do the hills while you enjoy the viewpoints, sit down for a coffee, and carry on at a gentle pace all day.",
          tips: [
            "Ride up, then walk only the flat or downhill parts",
            "Take the whole day with the full-day pass",
            "Not suitable for severe mobility impairments: you step up into the tuk tuk, and cobbles are bumpy",
          ],
        },
        {
          label: "On a budget",
          title: "A full day of Lisbon for €33",
          text: "The full-day pass gives you {stops} stops, a local driver-guide and as many rides as you like, for less than a private one-hour tuk tuk tour.",
          tips: [
            "Start when the tuk tuks open to make the most of your day",
            "Pack a picnic for the Graça or Senhora do Monte viewpoints",
            "The pass is per person: no surprise extras",
          ],
        },
      ],
    },
    route: {
      eyebrow: "The route",
      title: "{count} iconic stops",
      lead: "Call from any of the pickup stops and a tuk tuk will come to take you wherever you want to go next.",
      pickup: "Pickup",
      legend: "= you can request a tuk tuk here",
    },
    comparison: {
      eyebrow: "Why the pass",
      title: "The best of a bus and a tuk tuk tour, in one ticket",
      feature: "Feature",
      swipe: "Swipe to compare →",
      columns: ["Lisbon Visitor Pass", "Hop-on hop-off bus", "Private tuk tuk tour"],
      rows: [
        { label: "Gets up to the hilltop viewpoints", values: ["Yes", "Main avenues only", "Yes"] },
        { label: "Local guide on board", values: ["Yes", "No", "Yes"] },
        { label: "Hop off, explore, hop back on", values: ["Yes, all day", "Yes", "No, fixed 1–4h"] },
        { label: "Need to book a time slot", values: ["No, just show up", "No", "Yes"] },
        { label: "Wait for the next ride", values: ["Usually under {wait} min", "30 minutes", "No wait"] },
        { label: "Group size", values: ["Small, max {max} per tuk tuk", "Large double-decker", "Your group only"] },
      ],
      bestFor: {
        label: "Best for",
        values: ["Exploring at your own pace", "Long distances on a budget", "Special occasions, full attention"],
      },
    },
    pricing: {
      eyebrow: "Choose your pass",
      title: "One price per person. Ride as often as you like.",
      lead: "Your time starts at your first ride, not when you buy, so you can book today and start whenever you're ready.",
      bestFor: "Best for:",
      plans: [
        {
          label: "4 hours",
          badge: "",
          valueNote: "€7.25 per hour",
          bestFor: "Cruise stops and short visits",
          points: ["The old town's main viewpoints", "Time for 2–3 longer stops", "Ideal before a ship's all-aboard time"],
        },
        {
          label: "Full day",
          badge: "Most popular",
          valueNote: "Best value",
          bestFor: "First time in Lisbon",
          points: ["Every stop, at an easy pace", "Time for a long lunch in Alfama", "Room to linger at the viewpoints"],
        },
      ],
      eticket: "E-ticket by email in minutes",
      checkout: "Secure card, Apple Pay & Google Pay checkout",
      privateNote: "Want a tuk tuk just for your group, with a set route?",
      privateLink: "See our private tuk tuk tours →",
    },
    reviews: {
      eyebrow: "Reviews",
      title: "Don't just take our word for it",
      lead: "Read what real travellers say about Guiders.pt on independent review sites.",
      links: ["Read reviews from travellers who booked the pass", "See what guests say about Guiders.pt"],
    },
    faq: {
      eyebrow: "Good to know",
      title: "Your questions, answered",
      items: [
        {
          q: "How is this different from a tuk tuk tour?",
          a: "A tour follows a fixed route with one group for 1–3 hours. The pass is hop-on hop-off: you ride between stops, explore on foot for as long as you like, and request the next tuk tuk when you're ready.",
        },
        {
          q: "How long will I wait for a tuk tuk?",
          a: "The average wait is under {wait} minutes. Request your ride from your e-ticket page a few minutes before you want to leave.",
        },
        {
          q: "Do I share the tuk tuk with other people?",
          a: "Rides can be shared, with up to {max} people per tuk tuk. If you'd rather have one to yourselves, book one of our private tuk tuk tours instead.",
        },
        {
          q: "When does my pass start?",
          a: "Your hours start at your first ride, not when you buy. A 4-hour pass started at 11:00 runs until 15:00. Unused passes are valid for {months} months.",
        },
        {
          q: "What are the opening hours?",
          a: "Tuk tuks run {hours}. Plan your last ride before then.",
        },
        {
          q: "Can I use it with children?",
          a: "Yes, children from {age} years old can ride. Infants under {age} can't, for safety reasons.",
        },
        {
          q: "Is it suitable for people with limited mobility?",
          a: "It saves a lot of walking up hills, but you need to step up into the tuk tuk and the cobbled streets are bumpy. It isn't suitable for severe mobility impairments. Message us if you're unsure and we'll help you decide.",
        },
        {
          q: "I'm on a cruise. Will I make it back in time?",
          a: "You decide when to finish, so you're never waiting on a group. The Panteão Nacional pickup stop is a short walk from Lisbon's cruise terminal. Leave plenty of margin before your all-aboard time.",
        },
        {
          q: "What if it rains?",
          a: "Tuk tuks have a roof but open sides, so bring a light jacket. Lisbon's viewpoints are still beautiful between showers.",
        },
        {
          q: "Where can I buy the pass?",
          a: "Online on this page, in minutes, or from one of our verified resellers in Lisbon. Your e-ticket arrives by email, so there's nothing to print.",
        },
      ],
      stillUnsure: "Still unsure?",
      whatsapp: "WhatsApp us",
      orEmail: "or email",
      happyToHelp: "We're happy to help you choose.",
    },
    finalCta: {
      title: "Ready to see Lisbon from the top?",
      lead: "Book in two minutes and ride today. Unused passes are valid for {months} months.",
    },
    sticky: {
      from: "From €{price}",
      hours: "4 hours or a full day",
    },
  },

  // Booking / e-ticket page (/ticket?email=...&orderNumber=...)
  ticket: {
    metaTitle: "Your booking | Guiders",
    nav: {
      request: "Request a ride",
      tickets: "Tickets",
      route: "Route",
      help: "Help",
    },
    lookup: {
      eyebrow: "Your booking",
      title: "Find your tickets",
      lead: "Enter the email you booked with and the order number from your confirmation email.",
      email: "Email",
      emailPlaceholder: "you@example.com",
      order: "Order number",
      orderPlaceholder: "From your confirmation email",
      submit: "Show my tickets",
      another: "Look up another booking",
      close: "Close",
    },
    loading: "Loading your booking…",
    notFound: {
      title: "We couldn't find that booking",
      tips: [
        "Use the same email address you booked with",
        "Copy the order number straight from your confirmation email",
        "Bought from a reseller? Use the email you gave them",
      ],
      help: "Still stuck? Send us your name and we'll find it for you.",
    },
    loadError: "We couldn't load your booking. Check your connection and try again.",
    retry: "Try again",
    greeting: "Hi {name}, here's your pass",
    ticketCount: "Tickets: {count}",
    status: {
      ready: "Ready to use",
      readyText: "Your time starts when the driver scans your ticket on your first ride.",
      active: "Active",
      activeText: "{time} left · valid until {until}",
      expired: "Expired",
      expiredText: "This pass ended on {until}.",
      inactive: "Not valid",
      inactiveText: "This booking can't be used for rides. Message us if you think this is a mistake.",
      startNow: "Starting now, your pass runs until {time}.",
      endsAtClosing: "Full day passes end when the tuk tuks stop at {time} and don't carry over to the next day.",
    },
    service: {
      open: "Tuk tuks are running now",
      openText: "Until {time}, Lisbon time.",
      closed: "Tuk tuks aren't running right now",
      closedText: "Service runs daily {hours}, Lisbon time. It's {now} in Lisbon.",
    },
    request: {
      title: "Request a ride",
      lead: "Tell us which stop you're at (or heading to) and we'll send a tuk tuk. The average wait is under {wait} minutes.",
      stop: "Pickup stop",
      chooseStop: "Choose a pickup stop",
      nearest: "Use my location",
      locating: "Finding you…",
      locationError: "We couldn't get your location. Please choose your stop from the list.",
      nearestFound: "Closest pickup stop: about {distance} away",
      people: "People riding",
      time: "Pickup time",
      asap: "As soon as possible",
      timeHint: "Lisbon time. Choose a time at least 30 minutes from now.",
      button: "Request on WhatsApp",
      closedButton: "Requests open at {time}",
      chooseFirst: "Choose a pickup stop first",
      tip: "Tip: request your ride a few minutes before you reach the stop, so the tuk tuk is there when you are.",
    },
    // New pickup request flow (components/ticket/PickupRequest.vue)
    pickup: {
      button: "Request a tuk tuk",
      sending: "Requesting…",
      at: "{stop} · people: {count}",
      contacting: "Contacting your driver…",
      contactingText: "Your request is with the tuk tuks on the road. This page updates by itself.",
      allBusy: "All tuk-tuks are busy, we're on it",
      allBusyText: "Our team has been alerted and will get a tuk tuk to you as soon as possible.",
      confirmed: "{name} confirmed, about {eta} min",
      confirmedNoEta: "{name} confirmed and on the way",
      finishing: "Your driver is finishing a ride, about {eta} min",
      arriving: "Your driver should be there any moment",
      vehicle: "Tuk tuk {vehicle}",
      onBoard: "You're on board. Enjoy the ride!",
      done: "Ride finished",
      doneText: "Hop off and explore. Request your next ride whenever you're ready.",
      cancelled: "Pickup cancelled",
      noDriver: "We couldn't find a driver",
      noDriverText: "Sorry about that. Message us on WhatsApp and we'll sort it out.",
      expired: "This request has ended",
      again: "Request another ride",
      cancel: "Cancel pickup",
      cancelConfirm: "Cancel this pickup?",
      passEnding: "Heads-up: your pass ends at {time}, possibly before a tuk tuk reaches you.",
      needHelp: "Need help?",
      error: "We couldn't send your request. Try again or message us on WhatsApp.",
      test: "Test mode",
    },
    tickets: {
      title: "Your tickets",
      lead: "One QR code per person. Show it to the driver on your first ride.",
      person: "Ticket {n} of {total}",
      validUntil: "Valid until {date}",
      notUsed: "Not used yet",
      brightness: "Turn up your screen brightness so the driver can scan it quickly.",
      screenshot: "Take a screenshot so you have your tickets even without signal.",
      copyLink: "Copy the link to this page",
      copied: "Link copied. Save it to find your tickets quickly.",
    },
    how: {
      title: "How your pass works",
      steps: [
        {
          title: "Request a ride",
          text: "Choose your pickup stop above and send the WhatsApp message. We'll confirm and send a tuk tuk.",
        },
        {
          title: "Show your QR code",
          text: "The driver scans one code per person on your first ride. That's when your time starts.",
        },
        {
          title: "Hop off and explore",
          text: "Take as long as you like at each stop. When you're ready, request your next ride the same way.",
        },
      ],
    },
    route: {
      title: "Route and pickup stops",
      lead: "Stops marked as pickup points are where you can request a tuk tuk.",
      summary: "{stops} stops · {pickups} pickup points",
      viewLabel: "Route view",
      allStops: "All stops",
      pickupOnly: "Pickup points only",
      pickupPoint: "Pickup point",
      stopLabel: "Stop",
      nearby: "Things to do nearby ({count})",
      listView: "List view",
      mapView: "Map view",
      directions: "Get directions",
      viewDetails: "View details",
      noAttractions: "No attractions listed for this stop",
      disclaimer:
        "Please note: activities shown here are run by trusted third-party providers. We're not responsible for their services, policies or experiences, so please check the operator's terms before booking.",
    },
    info: {
      title: "Good to know",
      whatToExpect: "What to expect",
      additionalInfo: "Additional information",
      notSuitableFor: "Not suitable for",
      accessibility: "Accessibility",
      cancellationPolicy: "Cancellation policy",
    },
    help: {
      title: "Need help?",
      lead: "Can't find the tuk tuk, running late or something not right? Message us and we'll sort it out.",
      whatsapp: "WhatsApp us",
      email: "Email support",
    },
  },
};
